import { fetchAuthSession } from "aws-amplify/auth";

import type { CustomerScope } from "./customerScope";
import {
  FOR_APPROVAL_FOLDER_NAME,
  RESULTS_FOLDER_NAME,
  SHARED_BY_EVIDO_FOLDER_NAME,
  UPLOAD_FOLDER_NAME,
} from "@/storage/folderNames";

export const AppRole = {
  Customer: "customer",
  Analyst: "analyst",
  QualityControl: "qualityControl",
} as const;

export type AppRole = (typeof AppRole)[keyof typeof AppRole];

export const ANALYST_GROUP_NAME = "analysts";
export const QUALITY_CONTROL_GROUP_NAME = "quality-control";

const READ_WRITE_DELETE_PERMISSIONS = ["get", "list", "write", "delete"];
const READ_WRITE_PERMISSIONS = ["get", "list", "write"];
const READ_ONLY_PERMISSIONS = ["get", "list"];

export type StoragePaths = Record<string, Record<string, string[]>>;

/**
 * Resolve the access tier from a user's `cognito:groups` claim.
 */
export function resolveRoleFromGroups(
  groups: readonly string[] | undefined,
): AppRole {
  const membership = new Set(groups ?? []);
  if (membership.has(QUALITY_CONTROL_GROUP_NAME)) {
    return AppRole.QualityControl;
  }
  if (membership.has(ANALYST_GROUP_NAME)) {
    return AppRole.Analyst;
  }
  return AppRole.Customer;
}

/** Read the signed-in user's access tier from their Cognito session token. */
export async function resolveRole(): Promise<AppRole> {
  const session = await fetchAuthSession();
  const claim = session.tokens?.idToken?.payload?.["cognito:groups"];
  const groups = Array.isArray(claim)
    ? claim.filter((group): group is string => typeof group === "string")
    : undefined;
  return resolveRoleFromGroups(groups);
}

/**
 * Build the Storage Browser `paths` for a role. These client paths only drive which
 * folders and actions the browser surfaces; the authoritative permissions are the
 * role's IAM policy (see infra/components/auth.py). Amplify makes each path its own
 * location, so staff take one coarse `<root>/*` and the listing toolbar decides which
 * folders offer upload and delete.
 */
export function buildStoragePaths(
  role: AppRole,
  customerScope: CustomerScope | undefined,
  projectPrefixRoot: string,
): StoragePaths {
  if (role === AppRole.Customer) {
    if (!customerScope?.projects.length) {
      throw new Error("A customer must belong to a project to browse files.");
    }
    const { organization, projects } = customerScope;
    return Object.fromEntries(
      projects.flatMap((project) => {
        const projectPrefix = `${projectPrefixRoot}/${organization}/${project}`;
        return [
          [
            `${projectPrefix}/${UPLOAD_FOLDER_NAME}/*`,
            { authenticated: READ_WRITE_PERMISSIONS },
          ],
          [
            `${projectPrefix}/${RESULTS_FOLDER_NAME}/*`,
            { authenticated: READ_ONLY_PERMISSIONS },
          ],
          [
            `${projectPrefix}/${SHARED_BY_EVIDO_FOLDER_NAME}/*`,
            { authenticated: READ_ONLY_PERMISSIONS },
          ],
        ];
      }),
    );
  }

  const allProjects = `${projectPrefixRoot}/*`;
  if (role === AppRole.Analyst) {
    return { [allProjects]: { authenticated: READ_WRITE_DELETE_PERMISSIONS } };
  }
  if (role === AppRole.QualityControl) {
    return { [allProjects]: { authenticated: READ_WRITE_DELETE_PERMISSIONS } };
  }
  throw new Error(
    `Unhandled role '${String(role)}' when building storage paths.`,
  );
}

export function isWithinForApprovalFolder(keyOrPrefix: string): boolean {
  return keyOrPrefix.includes(`/${FOR_APPROVAL_FOLDER_NAME}/`);
}
